/**
 * WingmanPage.jsx
 * 
 * Full-page AI Wingman assistant.
 * Features: Chat Help, Profile Review, LoveLink Coach
 * Route: /wingman
 */

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, MessageCircle, User, Link2, Copy, Check, Share2 } from 'lucide-react';
import { AuthContext } from '../App';
import apiClient from '../api/client';

export default function WingmanPage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [activeTab, setActiveTab] = useState('chat');
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Chat Help State
  const [chatMode, setChatMode] = useState('opener');
  const [chatTone, setChatTone] = useState('chill');
  const [conversationSnippet, setConversationSnippet] = useState('');
  const [draft, setDraft] = useState('');
  const [chatSuggestion, setChatSuggestion] = useState('');

  // Profile Review State
  const [profileText, setProfileText] = useState('');
  const [bio, setBio] = useState('');
  const [analysis, setAnalysis] = useState('');

  // LoveLink Script State
  const [channel, setChannel] = useState('sms');
  const [loveLinkTone, setLoveLinkTone] = useState('chill');
  const [loveLinkUrl, setLoveLinkUrl] = useState('');
  const [script, setScript] = useState('');

  useEffect(() => { fetchUsage(); }, []);

  const fetchUsage = async () => {
    try {
      const res = await apiClient.get('/wingman/usage', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsage(res.data);
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOrCopy = async (text) => {
    if (navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    copyToClipboard(text);
  };

  const handleError = (err) => {
    if (err.response?.status === 402) {
      return err.response.data.detail || "You've reached your free limit. Upgrade to AI Wingman Pro or Elite for more assists!";
    } else if (err.response?.status === 429) {
      return err.response.data.detail || 'Too many requests. Please try again in an hour.';
    } else if (err.response?.status === 500) {
      return 'AI assistant is temporarily unavailable. Please try again in a moment.';
    } else if (!err.response) {
      return 'Network error — check your connection and try again.';
    }
    return err.response?.data?.detail || 'Something went wrong. Please try again.';
  };

  const handleChatHelp = async () => {
    if (!conversationSnippet.trim()) return;
    setLoading(true);
    setChatSuggestion('');
    try {
      const res = await apiClient.post('/wingman/chat', {
        mode: chatMode, conversation_snippet: conversationSnippet, tone: chatTone,
        draft: chatMode === 'rewrite' ? draft : undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setChatSuggestion(res.data.suggestion);
      fetchUsage();
    } catch (err) {
      alert(handleError(err));
      fetchUsage();
    } finally { setLoading(false); }
  };

  const handleProfileReview = async () => {
    if (!profileText.trim() || !bio.trim()) return;
    setLoading(true);
    setAnalysis('');
    try {
      const res = await apiClient.post('/wingman/profile-review', {
        profile_text: profileText, bio,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAnalysis(res.data.analysis);
      fetchUsage();
    } catch (err) {
      alert(handleError(err));
      fetchUsage();
    } finally { setLoading(false); }
  };

  const handleLoveLinkScript = async () => {
    if (!loveLinkUrl.trim()) return;
    setLoading(true);
    setScript('');
    try {
      const res = await apiClient.post('/wingman/lovelink-script', {
        channel, tone: loveLinkTone, lovelink_url: loveLinkUrl,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setScript(res.data.script);
      fetchUsage();
    } catch (err) {
      alert(handleError(err));
      fetchUsage();
    } finally { setLoading(false); }
  };

  const tabs = [
    { key: 'chat', label: 'Chat Help', icon: MessageCircle },
    { key: 'profile', label: 'Profile Review', icon: User },
    { key: 'lovelink', label: 'LoveLink Coach', icon: Link2 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm transition"
            data-testid="wingman-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-rose-400" />
            <h1 className="text-lg font-bold">Unity Wingman</h1>
          </div>
          <div className="w-16" /> {/* spacer */}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 pb-16">
        {/* Usage Banner */}
        {usage && (
          <div className="mt-4 px-4 py-3 rounded-xl bg-slate-800/60 border border-slate-700 text-xs">
            {usage.has_unlimited ? (
              <span className="text-emerald-400 font-semibold">
                {usage.is_elite && usage.has_wingman_pro
                  ? 'Elite + AI Wingman Pro: Extended assists'
                  : usage.is_elite ? 'Elite: Extended assists'
                  : usage.has_wingman_pro ? 'AI Wingman Pro: Extended assists' : ''}
              </span>
            ) : (
              <div className="text-white/60">
                <span>Used <strong className="text-white">{usage.used_this_week}</strong> of <strong className="text-white">{usage.weekly_limit}</strong> free assists this week</span>
                {usage.remaining > 0 && <span className="text-emerald-400 ml-1">({usage.remaining} remaining)</span>}
                {usage.remaining === 0 && (
                  <p className="mt-2 text-rose-300 text-xs">
                    Upgrade to <strong className="text-white">AI Wingman Pro</strong> or <strong className="text-white">Elite</strong> for unlimited access.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="mt-6 flex rounded-xl bg-slate-800/50 p-1 gap-1" data-testid="wingman-tabs">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg flex items-center justify-center gap-1.5 transition ${
                activeTab === key
                  ? 'bg-rose-600 text-white shadow-lg shadow-rose-600/20'
                  : 'text-white/50 hover:text-white/80 hover:bg-slate-700/50'
              }`}
              data-testid={`wingman-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* Chat Help Tab */}
        {activeTab === 'chat' && (
          <div className="mt-6 space-y-4" data-testid="wingman-chat-section">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Mode</label>
              <select value={chatMode} onChange={(e) => setChatMode(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500">
                <option value="opener">Opener</option>
                <option value="reply">Reply</option>
                <option value="rewrite">Rewrite</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Tone</label>
              <select value={chatTone} onChange={(e) => setChatTone(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500">
                <option value="chill">Chill</option>
                <option value="confident">Confident</option>
                <option value="romantic">Romantic</option>
                <option value="funny">Funny</option>
                <option value="respectful">Respectful</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Conversation Snippet</label>
              <textarea value={conversationSnippet} onChange={(e) => setConversationSnippet(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500"
                rows="3" placeholder="Paste the recent conversation here..." />
            </div>
            {chatMode === 'rewrite' && (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Your Draft Message</label>
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500"
                  rows="2" placeholder="What do you want to say?" />
              </div>
            )}
            <button onClick={handleChatHelp} disabled={loading || !conversationSnippet.trim()}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition"
              data-testid="wingman-chat-submit">
              {loading ? 'Thinking...' : 'Get Suggestion'}
            </button>
            {chatSuggestion && (
              <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <p className="text-xs font-semibold text-white/40 mb-2">Suggested Message</p>
                <p className="text-white text-sm">{chatSuggestion}</p>
                <button onClick={() => copyToClipboard(chatSuggestion)}
                  className="mt-3 text-xs font-semibold text-rose-400 hover:text-rose-300 flex items-center gap-1">
                  {copied ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy to Clipboard</>}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Profile Review Tab */}
        {activeTab === 'profile' && (
          <div className="mt-6 space-y-4" data-testid="wingman-profile-section">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Profile Text (About Section)</label>
              <textarea value={profileText} onChange={(e) => setProfileText(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500"
                rows="3" placeholder="Paste your profile text here..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Bio</label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500"
                rows="2" placeholder="Your bio..." />
            </div>
            <button onClick={handleProfileReview} disabled={loading || !profileText.trim() || !bio.trim()}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition"
              data-testid="wingman-profile-submit">
              {loading ? 'Analyzing...' : 'Review My Profile'}
            </button>
            {analysis && (
              <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <p className="text-xs font-semibold text-white/40 mb-2">Wingman's Analysis</p>
                <p className="text-white text-sm whitespace-pre-wrap">{analysis}</p>
              </div>
            )}
          </div>
        )}

        {/* LoveLink Coach Tab */}
        {activeTab === 'lovelink' && (
          <div className="mt-6 space-y-4" data-testid="wingman-lovelink-section">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Channel</label>
              <select value={channel} onChange={(e) => setChannel(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500">
                <option value="sms">SMS / Text</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="messenger">Messenger</option>
                <option value="social">Social Post</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Tone</label>
              <select value={loveLinkTone} onChange={(e) => setLoveLinkTone(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500">
                <option value="chill">Chill</option>
                <option value="hype">Hype</option>
                <option value="romantic">Romantic</option>
                <option value="business">Business</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Your LoveLink URL</label>
              <input type="text" value={loveLinkUrl} onChange={(e) => setLoveLinkUrl(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-rose-500/50 focus:border-rose-500"
                placeholder="https://unitylove.app/ref/YOUR_CODE" />
            </div>
            <button onClick={handleLoveLinkScript} disabled={loading || !loveLinkUrl.trim()}
              className="w-full bg-rose-600 hover:bg-rose-700 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition"
              data-testid="wingman-lovelink-submit">
              {loading ? 'Crafting...' : 'Generate Invite Script'}
            </button>
            {script && (
              <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <p className="text-xs font-semibold text-white/40 mb-2">Your Invite Message</p>
                <p className="text-white text-sm break-words">{script}</p>
                <div className="mt-3 flex gap-3">
                  <button onClick={() => shareOrCopy(script)}
                    className="text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 px-3 py-1.5 rounded-lg transition flex items-center gap-1"
                    data-testid="wingman-lovelink-share-btn">
                    <Share2 className="w-3 h-3" />
                    {navigator.share ? 'Share' : 'Copy Message'}
                  </button>
                  <button onClick={() => copyToClipboard(script)}
                    className="text-xs font-semibold text-rose-400 hover:text-rose-300 flex items-center gap-1">
                    {copied ? <><Check className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
